import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Alert,
  Divider,
  Card,
  CardContent,
  Container,
  Fade,
  Slide,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  Person,
  Storage,
  Security,
  Warning,
  Email,
  CloudSync,
} from '@mui/icons-material';
import { FirebaseAdminService, UserStatusResponse } from '../../services/firebase-admin.service';

const UserStatusCheckPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<string>('');
  const [userStatus, setUserStatus] = useState<UserStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(null);
    setUserStatus(null);
    setLoadingStage('');
  };

  const handleCheckUserStatus = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setUserStatus(null);
    
    const loadingStages = [
      'Connecting to Firebase Authentication...',
      'Checking Firebase Realtime Database...',
      'Verifying Zoho CRM records...',
      'Compiling results...'
    ];
    
    try {
      // Simulate progressive loading stages
      for (let i = 0; i < loadingStages.length; i++) {
        setLoadingStage(loadingStages[i]);
        await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 400));
        
        // Make the actual API call on the last stage
        if (i === loadingStages.length - 1) {
          const userStatusResponse = await FirebaseAdminService.checkUserStatus(email);
          setUserStatus(userStatusResponse);
        }
      }
    } catch (error) {
      setError('Failed to check user status. Please try again.');
      console.error('Error checking user status:', error);
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  const renderStatusChip = (exists: boolean, label: string, size: 'small' | 'medium' = 'medium') => (
    <Chip
      icon={exists ? <CheckCircle /> : <Cancel />}
      label={label}
      color={exists ? 'success' : 'error'}
      variant="outlined"
      size={size}
      sx={{
        fontWeight: 600,
        borderWidth: 2,
        '& .MuiChip-icon': {
          fontSize: '1.2rem'
        }
      }}
    />
  );

  const renderInfoItem = (label: string, value: string | undefined, showMissing = false) => {
    if (!value && showMissing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Warning color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
            <strong>{label}:</strong> Missing
          </Typography>
        </Box>
      );
    }

    if (!value) return null;

    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        <strong>{label}:</strong> {value}
      </Typography>
    );
  };

  const renderStatusSection = (
    title: string,
    icon: React.ReactNode,
    exists: boolean,
    details?: React.ReactNode,
    color: 'primary' | 'secondary' | 'info' = 'primary'
  ) => (
    <Card 
      elevation={exists ? 4 : 2} 
      sx={{ 
        borderRadius: 3,
        transition: 'all 0.3s ease-in-out',
        border: exists ? `2px solid ${theme.palette.success.light}` : `1px solid ${theme.palette.grey[300]}`,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6]
        },
        background: exists 
          ? `linear-gradient(135deg, ${theme.palette.success.light}08, ${theme.palette.background.paper})`
          : theme.palette.background.paper
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: `${theme.palette[color].main}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {React.cloneElement(icon as React.ReactElement, { 
                  sx: { fontSize: '1.8rem', color: theme.palette[color].main }
                })}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                {title}
              </Typography>
            </Box>
            {renderStatusChip(exists, exists ? 'Found' : 'Not Found', 'small')}
          </Box>
          
          {exists && (
            <Fade in={exists}>
              <Box>
                <Divider sx={{ my: 1, opacity: 0.3 }} />
                {details && (
                  <Box sx={{ mt: 2 }}>
                    {details}
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderLoadingSkeleton = () => (
    <Fade in={loading}>
      <Stack spacing={3}>
        <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
          Checking Status for: <span style={{ color: theme.palette.primary.main }}>{email}</span>
        </Typography>
        
        {/* Loading Progress */}
        <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CloudSync 
                color="primary" 
                sx={{ 
                  fontSize: '2rem',
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Verification in Progress
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {loadingStage}
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    }
                  }} 
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Loading Skeletons */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3
          }}
        >
          {[1, 2, 3].map((index) => (
            <Card key={index} elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="70%" height={28} />
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ mt: 1, borderRadius: 1 }} />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" height={2} sx={{ borderRadius: 1 }} />
                  <Stack spacing={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="80%" />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Stack>
    </Fade>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            sx={{ 
              fontWeight: 800, 
              mb: 1,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center'
            }}
          >
            User Status Check
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ textAlign: 'center', fontWeight: 400 }}
          >
            Comprehensive user verification across all platforms
          </Typography>
        </Box>

        <Card elevation={3} sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Email color="primary" sx={{ fontSize: '2rem' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Email Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter an email address to check its status across Firebase Authentication, 
                    Firebase Realtime Database, and Zoho CRM
                  </Typography>
                </Box>
              </Box>
              
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                value={email}
                onChange={handleEmailChange}
                placeholder="user@example.com"
                error={!!error}
                helperText={error}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1.1rem'
                  }
                }}
              />
              
              <Button
                variant="contained"
                startIcon={loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} color="inherit" />
                  </Box>
                ) : <Search />}
                onClick={handleCheckUserStatus}
                disabled={loading}
                fullWidth={isMobile}
                size="large"
                sx={{ 
                  textTransform: 'none',
                  alignSelf: isMobile ? 'stretch' : 'flex-start',
                  borderRadius: 2,
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: loading 
                    ? `linear-gradient(45deg, ${theme.palette.grey[400]}, ${theme.palette.grey[500]})`
                    : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: loading
                      ? `linear-gradient(45deg, ${theme.palette.grey[400]}, ${theme.palette.grey[500]})`
                      : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  },
                  transition: 'all 0.3s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': loading ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${theme.palette.common.white}20, transparent)`,
                    animation: 'shimmer 1.5s infinite',
                    '@keyframes shimmer': {
                      '0%': { left: '-100%' },
                      '100%': { left: '100%' }
                    }
                  } : {}
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Verifying User...</span>
                  </Box>
                ) : 'Check User Status'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {error && (
          <Slide direction="down" in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Slide>
        )}

        {loading && renderLoadingSkeleton()}

        {!loading && userStatus && (
          <Fade in={!!userStatus} timeout={600}>
            <Stack spacing={3}>
              <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
                Status Results for: <span style={{ color: theme.palette.primary.main }}>{email}</span>
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 3
                }}
              >
                {renderStatusSection(
                  'Firebase Authentication',
                  <Security />,
                  userStatus.firebaseAuth.exists,
                  userStatus.firebaseAuth.exists && (
                    <Stack spacing={1}>
                      {renderInfoItem('User ID', userStatus.firebaseAuth.uid)}
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                        ✓ User authenticated and verified
                      </Typography>
                    </Stack>
                  ),
                  'primary'
                )}

                {renderStatusSection(
                  'Firebase Realtime Database',
                  <Storage />,
                  userStatus.firebaseRealtimeDb.exists,
                  userStatus.firebaseRealtimeDb.exists && (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                        ✓ User data found in database
                      </Typography>
                      {userStatus.firebaseRealtimeDb.data && (
                        <Typography variant="body2" color="text.secondary">
                          Database record contains user information
                        </Typography>
                      )}
                    </Stack>
                  ),
                  'secondary'
                )}

                {renderStatusSection(
                  'Zoho CRM',
                  <Person />,
                  userStatus.zohoCRM.exists,
                  userStatus.zohoCRM.exists && (
                    <Stack spacing={1}>
                      {renderInfoItem('Full Name', userStatus.zohoCRM.fullName)}
                      {renderInfoItem('Platform ID', userStatus.zohoCRM.platformId, true)}
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                        ✓ Customer record found
                      </Typography>
                    </Stack>
                  ),
                  'info'
                )}
              </Box>

              {/* Summary Section */}
              <Card 
                elevation={2} 
                sx={{ 
                  mt: 3, 
                  borderRadius: 3, 
                  background: `linear-gradient(135deg, ${theme.palette.grey[100]}, ${theme.palette.background.paper})`
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Status Summary
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    {renderStatusChip(userStatus.firebaseAuth.exists, 'Firebase Auth')}
                    {renderStatusChip(userStatus.firebaseRealtimeDb.exists, 'Database')}
                    {renderStatusChip(userStatus.zohoCRM.exists, 'CRM')}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default UserStatusCheckPage;